import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

interface BreadcrumbDetail {
  label: string;
  badge?: ReactNode;
  actions?: ReactNode;
  /**
   * Entrée de menu à surligner pendant que cette page est affichée, quand
   * l'URL ne suffit pas à la déduire.
   *
   * Cas d'usage : la fiche d'un bien en VENTE vit sous `/v2/biens/:id`,
   * partagée avec la fiche location. La sidebar surligne par préfixe d'URL,
   * elle allumait donc « Catalogue Location » alors que le fil d'Ariane
   * affiche « Catalogue Vente » (incohérence remontée par le PO 2026-08-07).
   * La page déclare ici son vrai rattachement.
   */
  activeNavPath?: string;
  /**
   * Optionnel : navigation prev/next dans la liste d'origine. Affiche deux
   * flèches dans le breadcrumb pour passer au bien suivant / précédent
   * sans revenir à la liste.
   */
  navigation?: {
    prevPath: string | null;
    nextPath: string | null;
    position: number;
    total: number;
  };
}

interface BreadcrumbContextType {
  detail: BreadcrumbDetail | null;
  setDetail: (detail: BreadcrumbDetail | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({
  detail: null,
  setDetail: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [detail, setDetail] = useState<BreadcrumbDetail | null>(null);
  return (
    <BreadcrumbContext.Provider value={{ detail, setDetail }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbDetail(detail: BreadcrumbDetail | null, deps: unknown[] = []) {
  const { setDetail } = useContext(BreadcrumbContext);
  useEffect(() => {
    setDetail(detail);
    return () => setDetail(null);
  }, [detail?.label, ...deps]);
}

export function useBreadcrumb() {
  return useContext(BreadcrumbContext);
}
